/**
 * Image Upload Service for S3 pre-signed URL uploads.
 * Replaces the Cloudinary upload widget with direct S3 uploads.
 *
 * Usage:
 *   import { uploadImage, deleteImage, getImageUrl } from './imageUploadService';
 *
 *   const result = await uploadImage(file, 'spaces', 'space-abc', {
 *     onProgress: (percent) => console.log(percent),
 *     maxRetries: 3,
 *     isPrimary: false,
 *   });
 */
import { BASE_URL, MEDIA_BASE_URL } from '../settings';
import { getAuthToken } from './authSession';
import axiosInstance from '../api/axiosInstance';

const IMAGE_API_URL = `${BASE_URL}ui/images`;

// Max sizes (bytes)
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;  // 10 MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100 MB

// Default max compression dimension
const MAX_COMPRESS_WIDTH = 2048;
const MAX_COMPRESS_HEIGHT = 2048;
const COMPRESS_QUALITY = 0.85;

/**
 * Request a pre-signed upload URL from the backend.
 */
export async function requestPresignedUrl({ imageSpace, referenceId, fileName, contentType, isPrimary = false, displayOrder = 0 }) {
  try {
    const response = await axiosInstance.post(`ui/images/presigned-url`, {
      imageSpace, referenceId, fileName, contentType, primary: isPrimary, displayOrder
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to get upload URL (${error.response?.status || 'Unknown error'})`);
  }
}

/**
 * Refresh a pre-signed URL for retry after expiry.
 */
export async function refreshPresignedUrl(imageId) {
  try {
    const response = await axiosInstance.post(`ui/images/${imageId}/refresh-url`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to refresh upload URL (${error.response?.status || 'Unknown error'})`);
  }
}

/**
 * Upload a file directly to S3 using a pre-signed PUT URL.
 * Returns a promise that resolves when upload is complete.
 *
 * @param {string} presignedUrl - The pre-signed S3 PUT URL
 * @param {File|Blob} file - The file to upload
 * @param {string} contentType - MIME type
 * @param {function} onProgress - Callback with progress percentage (0-100)
 */
export function uploadToS3(presignedUrl, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status });
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload was cancelled')));

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
}

/**
 * Compress an image file using canvas.
 * Returns a Blob (potentially smaller than the input).
 */
export async function compressImage(file, maxWidth = MAX_COMPRESS_WIDTH, maxHeight = MAX_COMPRESS_HEIGHT, quality = COMPRESS_QUALITY) {
  // Skip compression for non-image files or already small files
  if (!file.type.startsWith('image/') || file.size < 100 * 1024) {
    return file;
  }

  // Don't compress GIFs (they lose animation) or PNGs if they need transparency
  if (file.type === 'image/gif') {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Scale down if larger than max dimensions
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob && blob.size < file.size) {
            resolve(blob);
          } else {
            // Compressed version is larger — keep original
            resolve(file);
          }
        },
        file.type === 'image/png' ? 'image/png' : 'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // Fallback to original on error
    };

    img.src = url;
  });
}

/**
 * Full upload flow: compress → request pre-signed URL → upload to S3.
 * Includes retry logic with automatic URL refresh on 403 (expired).
 *
 * @param {File} file - The file to upload
 * @param {string} imageSpace - e.g. "spaces", "blog"
 * @param {string} referenceId - e.g. spaceId, blogPostId
 * @param {object} options
 * @param {function} options.onProgress - Progress callback (0-100)
 * @param {number} options.maxRetries - Max retry attempts (default 3)
 * @param {boolean} options.isPrimary - Whether this is the primary image
 * @param {number} options.displayOrder - Display order in gallery
 * @param {boolean} options.compress - Whether to compress before upload (default true)
 * @returns {Promise<object>} Upload result with imageId, s3Key, publicUrl
 */
export async function uploadImage(file, imageSpace, referenceId, options = {}) {
  const {
    onProgress,
    maxRetries = 3,
    isPrimary = false,
    displayOrder = 0,
    compress = true,
    onWarning,
  } = options;

  // Validate file size
  const isVideo = file.type.startsWith('video/');
  const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
  if (file.size > maxSize) {
    throw new Error(`File too large. Maximum size is ${isVideo ? '100MB' : '10MB'}.`);
  }

  // Validate image resolution (spaces only)
  // Hard reject: < 800x533  |  Soft warn: 800x533–1023x682  |  OK: ≥ 1024x683
  if (!isVideo && imageSpace === 'spaces') {
    await new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const { width, height } = img;
        if (width < 800 || height < 533) {
          // Hard reject — too low
          reject(new Error(
            `Image resolution too low (${width}x${height}px). Minimum required is 800x533px.`
          ));
        } else if (width < 1024 || height < 683) {
          // Soft warn — below recommended but acceptable
          if (typeof onWarning === 'function') {
            onWarning(
              `Image resolution (${width}x${height}px) is below the recommended 1024x683px. It may appear less sharp on larger screens.`
            );
          }
          resolve();
        } else {
          resolve();
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Invalid image file'));
      };
    });
  }

  // Validate video duration (spaces only: max 180s)
  if (isVideo && imageSpace === 'spaces') {
    try {
      await new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = URL.createObjectURL(file);
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          if (video.duration > 180) {
            reject(new Error(`Video duration exceeds the maximum limit of 3 minutes (got ${Math.round(video.duration)}s)`));
          } else {
            resolve();
          }
        };
        video.onerror = () => {
          URL.revokeObjectURL(video.src);
          reject(new Error('Invalid or unplayable video file'));
        };
      });
    } catch (err) {
      throw err;
    }
  }

  // Compress image (skip for videos)
  let uploadFile = file;
  if (compress && !isVideo) {
    try {
      uploadFile = await compressImage(file);
    } catch (err) {
      console.warn('Image compression failed, uploading original:', err);
      uploadFile = file;
    }
  }

  // Request pre-signed URL
  const urlResponse = await requestPresignedUrl({
    imageSpace,
    referenceId,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    isPrimary,
    displayOrder,
  });

  const { imageId, presignedUrl, s3Key, publicUrl } = urlResponse;

  // Upload with retry
  let lastError = null;
  let currentUrl = presignedUrl;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      await uploadToS3(currentUrl, uploadFile, file.type || 'application/octet-stream', onProgress);

      // Success!
      return {
        imageId,
        s3Key,
        publicUrl,
        url: s3Key, // Store relative S3 path for DB storage
        resource_type: isVideo ? 'video' : 'image',
        asset_id: imageId,
        original_filename: file.name,
        format: _extractExtension(file.name),
        bytes: uploadFile.size,
      };
    } catch (err) {
      lastError = err;
      console.warn(`Upload attempt ${attempt}/${maxRetries} failed:`, err.message);

      if (attempt < maxRetries) {
        // If it's a 403 (expired URL), refresh the pre-signed URL
        if (err.message.includes('403') || err.message.includes('expired')) {
          try {
            const refreshed = await refreshPresignedUrl(imageId);
            currentUrl = refreshed.presignedUrl;
          } catch (refreshErr) {
            console.error('Failed to refresh pre-signed URL:', refreshErr);
          }
        }

        // Brief delay before retry
        await _delay(1000 * attempt);
      }
    }
  }

  throw lastError || new Error('Upload failed after all retry attempts');
}

/**
 * Upload a Blob (e.g. from AI enhancement) to S3.
 * This replaces the old `uploadBlobToCloudinary` function.
 */
export async function uploadBlobToS3(blob, fileName, imageSpace, referenceId, options = {}) {
  const file = new File([blob], fileName, { type: blob.type || 'image/png' });
  return uploadImage(file, imageSpace, referenceId, options);
}

/**
 * Delete an image from S3 and the database.
 */
export async function deleteImage(imageId) {
  try {
    await axiosInstance.delete(`ui/images/${imageId}`);
  } catch (error) {
    if (error.response?.status !== 404) {
      throw new Error(`Failed to delete image (${error.response?.status || 'Unknown error'})`);
    }
  }
}

/**
 * List all completed images for a reference entity.
 */
export async function listImages(imageSpace, referenceId) {
  try {
    const response = await axiosInstance.get(`ui/images/by-reference/${imageSpace}/${referenceId}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to list images (${error.response?.status || 'Unknown error'})`);
  }
}

/**
 * Get the public URL for an S3 key (via CloudFront).
 */
export function getImageUrl(s3Key) {
  const baseUrl = MEDIA_BASE_URL.endsWith('/') ? MEDIA_BASE_URL : `${MEDIA_BASE_URL}/`;
  return `${baseUrl}${s3Key}`;
}

// --- Internal helpers ---

function _getAuthHeaders() {
  const token = getAuthToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

function _extractExtension(fileName) {
  if (!fileName || !fileName.includes('.')) return 'jpg';
  return fileName.split('.').pop().toLowerCase();
}

function _delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generate a 24-character hexadecimal MongoDB-style ID.
 */
export function generateObjectId() {
  const timestamp = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
  const randomBytes = Array.from({ length: 16 }, () => 
    Math.floor(Math.random() * 16).toString(16)
  ).join('');
  return timestamp + randomBytes;
}
